import {useEffect, useRef} from 'react';

import {useInView} from 'motion/react';

import {useSetCurrentSection} from 'Application/useCases/useSetCurrentSection';

/**
 * Custom React hook that manages scroll-based anchor routing behavior by observing a DOM element's visibility.
 * This hook tracks whether the referenced element is in the viewport using an intersection observer. Once the element
 * is in view and the route change is considered complete, it updates the router path to reflect the anchor if necessary.
 * It ensures the scroll position is preserved and the route update is shallow to avoid unnecessary re-renders.
 *
 * @param linkId An optional anchor link (e.g., `#section1`) to match against the current route path.
 * @returns An object containing a `ref` which should be attached to the target DOM element to be observed.
 *
 * @example
 * ```tsx
 * const {ref} = useScrollAnchorWrapper('#section1');
 *
 * return <div ref={ref}>Anchored Section</div>;
 * ```
 */
export const useScrollAnchorWrapper = (linkId?: `#${string}` | null) => {
    const {setCurrentSection} = useSetCurrentSection();
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, {margin: '-50% 0px -50% 0px'});

    useEffect(() => {
        setCurrentSection(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isInView && linkId) {
            setCurrentSection(linkId as string);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInView, linkId]);

    return {ref};
};